import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { useToastContext } from '../../../../../../providers/ToastProvider/Toast.hook.ts';
import { eAlertType } from '../../../../../../providers/ToastProvider/Toast.enum.ts';
import { useWorkspaceContext } from '../../../../../../providers/WorkspaceProvider/Workspace.context.ts';
import { useModalsContext } from '../../../../../../providers/ModalsProvider/Modals.context.ts';
import { useUserContext } from '../../../../../../providers/UserProvider/User.context.ts';
import { eChannelType } from '../../../../../../providers/SocketsProvider/Sockets.enums.ts';
import { ChatEnum, EventType, MediaType } from '../../../../Chat.enum.ts';
import { useMessageBarContext } from '../../MessageBar.hook.ts';
import { useChatContext } from '../../../../Chat.hook.ts';
import { ChatService } from '../../../../Chat.service.ts';
import { useWapiPlanSendGuard } from 'src/components/Chat/useWapiPlanSendGuard.hook';
import { tAttachment, tChatEvent, tNoteAttachment, tWapiMedia, tWapiSendTemplateRequest, tWapiTemplateParameter } from '../../../../Chat.types.ts';
import { tComponent } from '../../../../../../pages/WhatsappApi/WhatsappApi.types.ts';
import { eComponentType, eFormatType } from '../../../../../../pages/WhatsappApi/WhatsappApi.enum.ts';
import { buildTemplateMedia } from 'src/components/Chat/components/MessageBar/components/WabTemplatesVariablesAction/WabTemplatesVariablesAction.functions.ts';
import { isSafeTemplateHeaderMediaUrl, templateHeaderSendMediaType } from 'src/utils/templateMedia.ts';
import { isPredefinedTemplateVariable } from 'src/constants/templateVariables.constants.ts';
import {
  buildOutboundRefusalAlert,
  lookupOutboundRefusal,
} from 'src/utils/ErrorDirectory.ts';
import { tWabTemplateScheduleActionContext } from './WabTemplateScheduleAction.types.ts';

const SCHEDULE_FAILED_MESSAGE =
  'No se pudo programar la plantilla. Inténtalo de nuevo.';

// BUG-741: a timeout is NOT a failure — the server may have committed the
// campaign after we stopped listening (the axios timeout is 30s and this
// endpoint does a WABA lookup, a template lookup, two inserts and a
// leads-service enrichment call bounded at 10s). Telling the agent it failed is
// what induced the retry that double-sent the template. WBS is now idempotent
// for an identical resubmit, so retrying is safe — but the copy must stop
// asserting an outcome we do not know.
const SCHEDULE_UNCONFIRMED_MESSAGE =
  'No pudimos confirmar si la plantilla quedó programada. Revisa los mensajes programados del chat antes de intentarlo de nuevo.';

// BUG-741: WBS returns 409 when a byte-identical schedule is already in flight
// (a double-click, or a retry that overlapped the original). Nothing is wrong and
// no duplicate was created — the first request is completing — so this must not
// read as a failure.
const SCHEDULE_IN_FLIGHT_MESSAGE =
  'Esta plantilla ya se está programando para este contacto. Revisa los mensajes programados del chat.';

function statusOf(error: unknown): number | undefined {
  const axiosError = error as
    | { response?: { status?: number }; status?: number }
    | undefined;
  return axiosError?.response?.status ?? axiosError?.status;
}

/**
 * True when the request never received an HTTP response — a timeout, an abort,
 * or a network failure. In those cases the server's outcome is genuinely
 * unknown. A response with a status means the server answered and rejected, so
 * the schedule really did not happen.
 */
function isUnconfirmedOutcome(error: unknown): boolean {
  const axiosError = error as
    | { response?: { status?: number }; status?: number; code?: string }
    | undefined;

  if (statusOf(error)) return false;

  return (
    axiosError?.code === 'ECONNABORTED' ||
    axiosError?.code === 'ETIMEDOUT' ||
    axiosError?.code === 'ERR_NETWORK' ||
    axiosError?.code === 'ERR_CANCELED' ||
    // No response and no recognized transport code: still an answer we never
    // got, so treat it as unknown rather than claiming failure.
    axiosError?.response === undefined
  );
}

function parseComponents(raw: string): tComponent[] {
  try {
    return JSON.parse(raw) as tComponent[];
  } catch {
    return [];
  }
}

function extractBody(components: tComponent[]): string {
  return components.find((c) => c.type === 'BODY')?.text || '';
}

function extractHeader(components: tComponent[]): string | null {
  const h = components.find((c) => c.type === 'HEADER');
  if (!h) return null;
  return h.text ?? h.example?.url_example?.[0] ?? h.example?.header_handle?.[0] ?? null;
}

function extractVariableNames(body: string, header: string | null): string[] {
  const namedVarRegex = /{{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*}}/g;
  const names = new Set<string>();
  let match;
  const sources = [body, header ?? ''];
  for (const src of sources) {
    while ((match = namedVarRegex.exec(src)) !== null) {
      names.add(match[1]);
    }
  }
  return Array.from(names);
}

/**
 * Returns the header example's media URL, but only when Leadsales hosts it.
 *
 * Templates created through our own flow store it in `url_example`; templates
 * synced from Meta Business Manager carry it in `header_handle`, which for media
 * headers is one of Meta's own CDN URLs. Those are **not** fetchable by Meta as a
 * send link — its fetcher answers 403 — so scheduling one produced a send that
 * was guaranteed to fail hours later. The immediate send has refused them since
 * BUG-817; this path now agrees, so Enviar and the Clock button no longer
 * disagree on the same template row.
 *
 * Both sources cross the same gate the immediate send uses, which also rejects
 * non-https values and opaque upload handles, so no local host check is needed
 * here. An unusable `url_example` falls through to `header_handle` rather than
 * short-circuiting, matching `resolveTemplateHeaderMediaUrl`. (BUG-589, BUG-818)
 */
function headerExampleUrl(header: tComponent): string | undefined {
  const url = header.example?.url_example?.[0];
  if (url && isSafeTemplateHeaderMediaUrl(url)) return url;
  const handle = header.example?.header_handle?.[0];
  if (handle && isSafeTemplateHeaderMediaUrl(handle)) return handle;
  return undefined;
}

/**
 * Builds the header media for a scheduled template send from the template's own
 * static header example, mirroring the immediate send (WabTemplatesVariablesAction).
 * The scheduling UI has no document uploader, so the template example is the only
 * media source. Without this the scheduled send dropped the header media and Meta
 * rejected it with 132012, failing silently (BUG-589). Returns undefined for
 * text-only headers or when the example carries no public URL.
 */
function extractHeaderMedia(components: tComponent[]): tWapiMedia | undefined {
  const header = components.find((c) => c.type === eComponentType.HEADER);
  if (!header) return undefined;
  const url = headerExampleUrl(header);
  if (!url) return undefined;
  // BUG-866: el mismo `switch` sobre `format` que ahora gatea el chat 1:1 y
  // el modal. Vivía privado aquí; se promovió a src/utils/templateMedia.ts
  // sin cambiar lo que este camino resuelve.
  const mediaType = templateHeaderSendMediaType(header.format, url);
  if (!mediaType) return undefined;
  return buildTemplateMedia({ mediaType, url, fileName: '' });
}

/**
 * Whether the template's header declares media at all — i.e. whether Meta will
 * expect a header component on the send.
 *
 * Paired with `extractHeaderMedia`: a header that declares media but resolves to
 * none is unschedulable, because the launch cron builds the header component
 * solely from the stored media and Meta answers 132012 ("parameter format
 * mismatch") when the template declares a media header and the send carries
 * none. That is the failure BUG-589's fallback existed to prevent, and it is
 * exactly what BUG-818's host gate would otherwise have reintroduced — trading
 * a 403 for a 132012 rather than fixing the send. (BUG-818)
 */
function headerDeclaresMedia(components: tComponent[]): boolean {
  const header = components.find((c) => c.type === eComponentType.HEADER);
  if (!header) return false;
  switch (header.format) {
    case eFormatType.IMAGE:
    case eFormatType.VIDEO:
    case eFormatType.DOCUMENT:
    case eFormatType.MEDIA:
      return true;
    default:
      return false;
  }
}

/**
 * Maps a WAPI header media kind to the chat MediaType + a representative mime.
 * The mime drives the document-vs-media gate in the bubble (image/video render
 * from the url regardless), so only the document case needs an exact value.
 * Returns undefined for unknown kinds.
 */
function headerMediaAttachmentMeta(
  type: string,
): { mediaType: MediaType; mimeType: string } | undefined {
  switch (type) {
    case 'image':
      return { mediaType: MediaType.IMAGE, mimeType: 'image/jpeg' };
    case 'video':
      return { mediaType: MediaType.VIDEO, mimeType: 'video/mp4' };
    case 'document':
      return { mediaType: MediaType.DOCUMENT, mimeType: 'application/pdf' };
    default:
      return undefined;
  }
}

export default function useWabTemplateScheduleAction(): tWabTemplateScheduleActionContext {
  const { addAlert } = useToastContext();
  // Only for the copy of a schedule the server refused.
  const { t } = useTranslation('errors/errors');
  const { currentWorkspaceId } = useWorkspaceContext();
  const { selectedLeadForChatModal } = useModalsContext();
  const { currentUserId } = useUserContext();
  const { selectedChat, chatEvents, setChatEvents, followUps, setFollowUps } = useChatContext();
  const { blockWapiPlanSend } = useWapiPlanSendGuard();
  const { setSelectedAction, scheduledWapiTemplate, setScheduledWapiTemplate } =
    useMessageBarContext();

  const [loading, setLoading] = useState(false);
  const [triggerDate, setTriggerDate] = useState<string | undefined>();
  const [triggerTime, setTriggerTime] = useState<string | undefined>();
  const [paramValues, setParamValues] = useState<Record<string, string>>({});
  const [touchedParams, setTouchedParams] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setTriggerDate(undefined);
    setTriggerTime(undefined);
    setParamValues({});
    setTouchedParams({});
  }, [selectedChat?.id]);

  const localDate = moment().format('YYYY-MM-DD');
  const localTime = moment().format('HH:mm');

  const { bodyText, headerText, headerMedia, headerMediaMissing, parameters } = useMemo(() => {
    if (!scheduledWapiTemplate?.components) {
      return {
        bodyText: '',
        headerText: null,
        headerMedia: undefined,
        headerMediaMissing: false,
        parameters: [],
      };
    }
    const parsed = parseComponents(scheduledWapiTemplate.components);
    const body = extractBody(parsed);
    const header = extractHeader(parsed);
    const varNames = extractVariableNames(body, header);
    const manualVarNames = varNames.filter((name) => !isPredefinedTemplateVariable(name));
    const media = extractHeaderMedia(parsed);
    return {
      bodyText: body,
      headerText: header,
      headerMedia: media,
      // The template needs a header component we cannot supply. The schedule
      // modal has no uploader, so there is no way for the seller to resolve it
      // here — the send is blocked rather than queued to fail hours later.
      headerMediaMissing: !media && headerDeclaresMedia(parsed),
      parameters: manualVarNames.map((name) => ({ name })),
    };
  }, [scheduledWapiTemplate?.components]);

  const hasVariables = parameters.length > 0;

  const autoFillValues = useMemo<Record<string, string>>(() => ({
    nombre_del_lead: selectedLeadForChatModal?.name || '',
    'correo_electrónico': selectedLeadForChatModal?.email || '',
    correo_electronico: selectedLeadForChatModal?.email || '',
    empresa: selectedLeadForChatModal?.company || '',
    medio: selectedLeadForChatModal?.chat?.integration || '',
  }), [selectedLeadForChatModal]);

  const previewBody = useMemo(() => {
    let text = bodyText;
    Object.entries(autoFillValues).forEach(([key, value]) => {
      text = text.replace(
        new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'),
        value || `{{${key}}}`,
      );
    });
    Object.entries(paramValues).forEach(([key, value]) => {
      text = text.replace(
        new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'),
        value || `{{${key}}}`,
      );
    });
    return text;
  }, [bodyText, autoFillValues, paramValues]);

  const previewHeader = useMemo(() => {
    if (!headerText || headerText.startsWith('https://')) return headerText;
    let text = headerText;
    Object.entries(autoFillValues).forEach(([key, value]) => {
      text = text.replace(
        new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'),
        value || `{{${key}}}`,
      );
    });
    Object.entries(paramValues).forEach(([key, value]) => {
      text = text.replace(
        new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'),
        value || `{{${key}}}`,
      );
    });
    return text;
  }, [headerText, autoFillValues, paramValues]);

  const allVariablesFilled =
    !hasVariables ||
    parameters.every((p) => paramValues[p.name]?.trim());

  const isDateInPast = useMemo(
    () =>
      !!triggerDate &&
      !!triggerTime &&
      new Date(`${triggerDate}T${triggerTime}:00`) <= new Date(),
    [triggerDate, triggerTime],
  );

  const isScheduleDisabled =
    !triggerDate ||
    !triggerTime ||
    !allVariablesFilled ||
    loading ||
    isDateInPast ||
    headerMediaMissing;

  function handleParamChange(name: string, value: string) {
    setParamValues((prev) => ({ ...prev, [name]: value }));
  }

  function handleParamBlur(name: string) {
    setTouchedParams((prev) => ({ ...prev, [name]: true }));
  }

  async function handleScheduleTemplate() {
    if (
      !currentWorkspaceId ||
      !selectedLeadForChatModal?.handle ||
      !scheduledWapiTemplate?.template_id ||
      !triggerDate ||
      !triggerTime
    ) {
      return;
    }
    if (new Date(`${triggerDate}T${triggerTime}:00`) <= new Date()) return;
    // RET-2349 (KTD4): `scheduleWapiTemplate` always schedules by WhatsApp API,
    // whatever `channel_type` the follow-up card below is labelled with.
    if (blockWapiPlanSend(eChannelType.WHATSAPP_API)) return;

    const builtParameters: tWapiTemplateParameter[] = parameters.map((p) => ({
      name: p.name,
      value: paramValues[p.name] || '',
      type: bodyText.includes(`{{${p.name}}}`) ? 'body' : 'header',
      format: 'text',
    }));

    const triggerDateISO = new Date(`${triggerDate}T${triggerTime}:00`).toISOString();

    setLoading(true);

    const payload: tWapiSendTemplateRequest & { trigger_date: string } = {
      to: selectedLeadForChatModal.handle,
      template_id: scheduledWapiTemplate.template_id,
      parameters: builtParameters.length > 0 ? builtParameters : undefined,
      trigger_date: triggerDateISO,
      // BUG-589: attach the template's header document/image/video so the
      // scheduled send reproduces the same header as an immediate send.
      ...(headerMedia && { media: headerMedia }),
      // Attribute the scheduled send to the current user so WBS persists it on
      // the campaign and the template bubble shows the sender's name (INT-922).
      ...(currentUserId && { created_by: currentUserId }),
    };

    // BUG-687: WBS returns the ID of the campaign that will send the template.
    // Persisting it on the follow-up card is what lets "eliminar" cancel the
    // actual send — without it the card is deleted but the campaign fires.
    let campaignId: number | undefined;
    try {
      const scheduleResponse = await ChatService.scheduleWapiTemplate(
        currentWorkspaceId,
        payload,
      );
      // Optional: pre-BUG-687 WBS versions respond without campaign_id.
      // Accept both wire keys: the legacy gateway path (straight to WBS)
      // emits snake_case campaign_id; the bff-web schedule route emits
      // camelCase campaignId. Reading both keeps the cancel-on-delete link
      // alive if this call ever migrates routes.
      campaignId = scheduleResponse?.campaign_id ?? scheduleResponse?.campaignId;
    } catch (scheduleError) {
      // BUG-741: distinguish "the server said no" from "we never heard back".
      // Only the former is a confirmed failure.
      // 409 = an identical schedule is already in flight; not a failure.
      if (statusOf(scheduleError) === 409) {
        addAlert({
          type: eAlertType.WARNING,
          message: SCHEDULE_IN_FLIGHT_MESSAGE,
        });
        setLoading(false);
        return;
      }
      // 423: the outbound gate refused the schedule (the plan is paused, or
      // the send could not be tied to its workspace) and nothing was written.
      // Retrying cannot help, so it must not read as a plain failure.
      const refusal = lookupOutboundRefusal(scheduleError);
      if (refusal) {
        addAlert(buildOutboundRefusalAlert(refusal, t));
        setLoading(false);
        return;
      }
      const unconfirmed = isUnconfirmedOutcome(scheduleError);
      addAlert({
        type: unconfirmed ? eAlertType.WARNING : eAlertType.ERROR,
        message: unconfirmed
          ? SCHEDULE_UNCONFIRMED_MESSAGE
          : SCHEDULE_FAILED_MESSAGE,
      });
      setLoading(false);
      return;
    }

    let scheduleAlertMessage = 'Plantilla programada exitosamente.';
    let scheduleAlertType = eAlertType.SUCCESS;

    if (selectedChat) {
      const contentText = previewBody || (scheduledWapiTemplate.name ?? 'Plantilla programada');

      // BUG-589: surface the header media in the scheduled-template card too, not
      // just in the actual send. The follow-up bubble renders content.attachments,
      // so build both the persisted shape (tNoteAttachment) and the optimistic
      // local shape (tAttachment) from the same resolved header media.
      const attachmentMeta = headerMedia
        ? headerMediaAttachmentMeta(headerMedia.type)
        : undefined;
      const followUpAttachments: tNoteAttachment[] =
        headerMedia && attachmentMeta
          ? [{ media_type: attachmentMeta.mediaType, url: headerMedia.url, mimetype: attachmentMeta.mimeType }]
          : [];
      const previewAttachments: tAttachment[] =
        headerMedia && attachmentMeta
          ? [{
              vcard: null,
              decryption: null,
              duration: null,
              filename: headerMedia.filename ?? null,
              latitude: null,
              longitude: null,
              mediaType: attachmentMeta.mediaType,
              mimeType: attachmentMeta.mimeType,
              url: headerMedia.url,
            }]
          : [];

      try {
        const response = await ChatService.createFollowUp({
          chatId: selectedChat.id,
          data: {
            id: uuid(),
            channel_type: selectedLeadForChatModal.chat?.integration || eChannelType.WHATSAPP_API,
            content: { text: contentText, attachments: followUpAttachments },
            trigger_date: triggerDateISO,
            created_by: currentUserId ?? '',
            is_wapi_template: true,
            ...(campaignId && { campaign_id: campaignId }),
          },
        });
        const followUpId = response.id;

        const newEvent: tChatEvent = {
          id: followUpId,
          agendaEvent: null,
          chatID: selectedChat.id,
          type: EventType.FOLLOW_UP,
          content: { text: contentText, attachments: previewAttachments, linkPreview: null, quotedEventID: null },
          followUp: { triggerDate: triggerDateISO, consumed: false, campaignId: campaignId ?? null },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          deletedAt: '',
          message: null,
          note: null,
          createdBy: currentUserId ?? '',
          isWapiTemplate: true,
        };

        setFollowUps([newEvent, ...followUps]);
        setChatEvents([...chatEvents, newEvent]);
      } catch {
        scheduleAlertMessage = 'Plantilla programada, pero no se pudo crear la tarjeta de vista previa.';
        scheduleAlertType = eAlertType.WARNING;
      }
    }

    addAlert({ type: scheduleAlertType, message: scheduleAlertMessage });

    setLoading(false);
    setScheduledWapiTemplate(undefined);
    setParamValues({});
    setTouchedParams({});
    setTriggerDate(undefined);
    setTriggerTime(undefined);
    setSelectedAction(ChatEnum.CHAT);
  }

  return {
    loading,
    triggerDate,
    setTriggerDate,
    triggerTime,
    setTriggerTime,
    localDate,
    localTime,
    handleScheduleTemplate,
    paramValues,
    touchedParams,
    handleParamChange,
    handleParamBlur,
    parameters,
    previewBody,
    previewHeader,
    hasVariables,
    isScheduleDisabled,
    isDateInPast,
    headerMediaMissing,
  };
}
